/**
 * Billing & usage admin API client.
 *
 * All endpoints under /api/billing/admin/* are gated to the CerebralAdmin
 * role on the backend; the UI also hides the navigation entries when the
 * current user does not have that role.
 */

import { apiService } from './api';

const BASE = '/billing/admin';

const _qs = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      qs.append(k, String(v));
    }
  });
  return qs.toString();
};

export const billingApi = {
  // ── Installations ──────────────────────────────────────────────────────
  listInstallations: async (params = {}) => {
    const qs = _qs(params);
    const r = await apiService.get(`${BASE}/installations${qs ? `?${qs}` : ''}`);
    return r.data;
  },

  getInstallation: async (installationId) => {
    const r = await apiService.get(`${BASE}/installations/${installationId}`);
    return r.data;
  },

  createInstallation: async (payload) => {
    const r = await apiService.post(`${BASE}/installations`, payload);
    return r.data;
  },

  setInstallationStatus: async (installationId, payload) => {
    const r = await apiService.post(
      `${BASE}/installations/${installationId}/status`,
      payload,
    );
    return r.data;
  },

  // ── Usage ──────────────────────────────────────────────────────────────
  getInstallationUsage: async (installationId, params = {}) => {
    const qs = _qs(params);
    const r = await apiService.get(
      `${BASE}/installations/${installationId}/usage${qs ? `?${qs}` : ''}`,
    );
    return r.data;
  },

  // ── Audit ──────────────────────────────────────────────────────────────
  getInstallationAudit: async (installationId, params = {}) => {
    const qs = _qs(params);
    const r = await apiService.get(
      `${BASE}/installations/${installationId}/audit${qs ? `?${qs}` : ''}`,
    );
    return r.data;
  },

  // ── Subscriptions ──────────────────────────────────────────────────────
  listSubscriptionsForInstallation: async (installationId) => {
    const r = await apiService.get(
      `${BASE}/installations/${installationId}/subscriptions`,
    );
    return r.data;
  },

  createSubscription: async (installationId, payload) => {
    const r = await apiService.post(
      `${BASE}/installations/${installationId}/subscriptions`,
      payload,
    );
    return r.data;
  },

  // ── Pricing plans ──────────────────────────────────────────────────────
  listPricingPlans: async () => {
    const r = await apiService.get(`${BASE}/pricing-plans`);
    return r.data;
  },

  getPricingPlan: async (planId) => {
    const r = await apiService.get(`${BASE}/pricing-plans/${planId}`);
    return r.data;
  },

  createPricingPlan: async (payload) => {
    const r = await apiService.post(`${BASE}/pricing-plans`, payload);
    return r.data;
  },

  updatePricingPlan: async (planId, payload) => {
    const r = await apiService.put(`${BASE}/pricing-plans/${planId}`, payload);
    return r.data;
  },

  // ── Invoices ───────────────────────────────────────────────────────────
  listInvoices: async (params = {}) => {
    const qs = _qs(params);
    const r = await apiService.get(`${BASE}/invoices${qs ? `?${qs}` : ''}`);
    return r.data;
  },

  getInvoice: async (invoiceId) => {
    const r = await apiService.get(`${BASE}/invoices/${invoiceId}`);
    return r.data;
  },

  generateInvoice: async (payload) => {
    const r = await apiService.post(`${BASE}/invoices/generate`, payload);
    return r.data;
  },

  invoiceCsvUrl: (invoiceId) => `/api${BASE}/invoices/${invoiceId}/csv`,

  // ── Onboarding ─────────────────────────────────────────────────────────
  onboardCustomer: async (payload) => {
    const r = await apiService.post(`${BASE}/onboard`, payload);
    return r.data;
  },
};

export default billingApi;
